import { useCallback } from 'react';
import Papa from 'papaparse';
import { Scenario } from '../types';
import { useNavigation, Screen } from '../contexts/NavigationContext';
import { useTestSession } from '../contexts/TestSessionContext';
import { useProjects } from '../contexts/ProjectsContext';
import { useToast } from '../contexts/ToastContext';
import { groupScenariosByCategory } from '../utils/scenarioUtils';

const API_BASE_URL = process.env.REACT_APP_API_HOST;

export function useHandleNavigate() {
  const {
    setCurrentScreen,
    setPreviousScreen,
    currentScreen,
    setIsAnalyzing,
  } = useNavigation();
  const { setScenarios, setCategories, setCurrentTest } = useTestSession();
  const { showToast } = useToast();
  const { projects, setSelectedProject } = useProjects();

  const handleNavigate = useCallback(
    async (screen: string, testId?: string, projectId?: string) => {
      setPreviousScreen(currentScreen);
      setCurrentScreen(screen as Screen);

      if (screen === 'test' || screen === 'home') {
        setIsAnalyzing(false);
      }

      switch (screen) {
        case 'home':
          window.history.pushState({}, '', '/home');
          break;
        case 'test':
          if (projectId && testId) {
            window.history.pushState({}, '', `/test/${projectId}/${testId}`);
          } else if (testId) {
            window.history.pushState({}, '', `/test/${testId}`);
          } else {
            window.history.pushState({}, '', '/test');
          }
          break;
        case 'settings':
          window.history.pushState({}, '', '/settings');
          break;
        case 'login':
        case 'register':
          window.history.pushState({}, '', '/login');
          break;
        case 'analysis':
          if (projectId) {
            window.history.pushState({}, '', `/analysis/${projectId}`);
          }
          break;
      }

      if (testId) {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(
            `${API_BASE_URL}/api/tests/test_results/${testId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(
              `Błąd HTTP: ${response.status} - ${errorData?.error || response.statusText}`
            );
          }
          const test = await response.json();
          if (!test.filename || !test.scenarioResults || !test.metadata) {
            throw new Error('Nieprawidłowa struktura danych testu');
          }

          const csvResponse = await fetch(
            `${API_BASE_URL}/api/tests/scenarios/${test.filename}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!csvResponse.ok) throw new Error('Nie udało się pobrać pliku CSV');
          const csvData = await csvResponse.text();

          Papa.parse(csvData, {
            header: true,
            complete: (results) => {
              if (!results.data?.length) {
                throw new Error('Plik CSV jest pusty lub nie zawiera danych');
              }
              let scenariosData = results.data as Scenario[];
              if (
                test.metadata.selectedCategories?.length &&
                !test.metadata.isFullRegression
              ) {
                scenariosData = scenariosData.filter(s =>
                  test.metadata.selectedCategories!.includes(s.Kategoria)
                );
              }
              const scenariosWithResults = scenariosData
                .filter(s => {
                  const exists = test.scenarioResults[s['ID testu']] !== undefined;
                  const isDeleted = s.isDeleted === 'true';
                  return exists && !isDeleted;
                })
                .map(s => ({
                  ...s,
                  Status: test.scenarioResults[s['ID testu']]?.Status || 'Not Tested',
                  Notes: test.scenarioResults[s['ID testu']]?.Notes || '',
                }));
              setScenarios(scenariosWithResults);
              setCategories(groupScenariosByCategory(scenariosWithResults));
              setCurrentTest(test);
            },
            error: () => {
              showToast('Błąd podczas parsowania CSV. Sprawdź format pliku.', 'error');
            },
          });
        } catch (error) {
          showToast(
            'Nie udało się załadować ostatniego testu. Spróbuj ponownie.',
            'error'
          );
          localStorage.removeItem('lastTestId');
        }
      }
    },
    [
      currentScreen,
      setCurrentScreen,
      setPreviousScreen,
      setIsAnalyzing,
      setScenarios,
      setCategories,
      setCurrentTest,
      showToast,
    ]
  );
  return { handleNavigate, projects, setSelectedProject };
}
